import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';

const CONDITIONS = ['new', 'used', 'all'];
const SERVICES = [
  { v: '', label: '— none —' },
  { v: 'photos_and_video', label: 'Photo + Video' },
  { v: 'photos_only', label: 'Photo only' },
  { v: 'video_only', label: 'Video only' },
];
const blankForm = {
  dealership_id: '', labor_code: '', description: '',
  labor_price: '', payout_price: '', condition: 'all',
  auto_detect_type: '', taxable: false,
};

export default function AdminRates() {
  const navigate = useNavigate();
  const [dealers, setDealers] = useState([]);
  const [dealerId, setDealerId] = useState('');   // '' = all rate cards
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(null);   // null | 'new' | code object
  const [isClone, setIsClone] = useState(false);  // true when 'new' came from Clone
  const [form, setForm] = useState(blankForm);
  const [view, setView] = useState('sheet');      // 'sheet' (default landing) | 'list'
  const [copyFrom, setCopyFrom] = useState('');    // dealer id to copy rates from
  const [reorder, setReorder] = useState(false);   // reorder mode for the product list
  // ⭐ SHOW EVERY PRODUCT BY DEFAULT, applied or not.
  //
  // ⚠️⚠️ THIS DEFAULTED TO FALSE AND IT COST US A RATE SHEET. The intent was a clean
  // "confirm their existing codes" view, revealing the rest only when adding — but hiding the
  // unapplied rows means a dealer with 5 of 23 products applied shows FIVE ROWS, EVERY BOX
  // ALREADY TICKED, and no empty box anywhere. Kim, 2026-10-02, trying to re-add a code:
  // *"she says edit clone or delete but no check to add"*. She was right. The only way in was
  // a blue `+ Add codes (18 available)` button in a right-aligned header span, and the person
  // who uses this screen more than anyone could not find it.
  //
  // Unapplied rows were ALREADY built to read as inactive when shown — greyed text, grey
  // input backgrounds, disabled fields (see `dim` / `inpStyle` below) — so the clutter the
  // default was protecting against does not really exist. Only the default was wrong.
  // "Hide unapplied" is still there for anyone who wants the narrow view.
  const [showInactive, setShowInactive] = useState(true);
  const [sheetDealer, setSheetDealer] = useState('');
  const [sheet, setSheet] = useState([]);
  const [sheetBusy, setSheetBusy] = useState(false);
  const [sla, setSla] = useState('');          // per-dealer SLA override ('' = business default)
  const [slaBusy, setSlaBusy] = useState(false);
  const [stickers, setStickers] = useState(false);   // dealer uses window stickers
  const [stickersBusy, setStickersBusy] = useState(false);
  const importRef = useRef(null);
  const [omitMode, setOmitMode] = useState('skip');  // 'skip' | 'deactivate' for omitted CSV rows

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('adminAuth');
    if (!token) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // An expired/invalid token returns 401 — bounce to login instead of silently
  // emptying the page (the token only proves it EXISTS in localStorage, not valid).
  const json = useCallback((r) => {
    if (r.status === 401 || r.status === 403) {
      localStorage.removeItem('adminAuth');
      navigate('/admin/login');
      throw new Error('session expired');
    }
    return r.json();
  }, [navigate]);

  useEffect(() => {
    const h = authHeader();
    if (!h) return;
    fetch(`${API_BASE}/admin/dealers?limit=500`, { headers: h })
      .then(json)
      .then(d => setDealers((Array.isArray(d) ? d : (d.dealers || d.dealerships || []))))
      .catch(() => setError('Failed to load dealers'));
  }, [authHeader]);

  const loadCodes = useCallback(() => {
    const h = authHeader();
    if (!h) return;
    setLoading(true);
    const qs = dealerId ? `?dealership_id=${dealerId}` : '';
    fetch(`${API_BASE}/photographer/labor-codes${qs}`, { headers: h })
      .then(json)
      .then(d => setCodes(Array.isArray(d) ? d : []))
      .catch(() => setError('Failed to load rates'))
      .finally(() => setLoading(false));
  }, [authHeader, dealerId]);

  useEffect(() => { loadCodes(); }, [loadCodes]);

  const dealerName = (id) => {
    if (!id) return 'Default (all dealers)';
    const d = dealers.find(x => String(x.id) === String(id));
    return d ? (d.dealership_name || d.name) : `#${id}`;
  };

  // Normalize a money value to 2 decimals for the grid ('' stays blank; junk passes
  // through untouched so the user can keep typing). Used on load, copy, and blur.
  const fmt2 = (v) => {
    if (v === '' || v == null) return '';
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : v;
  };

  const openAdd = () => {
    setForm({ ...blankForm, dealership_id: dealerId || '' });
    setIsClone(false);
    setEditing('new');
  };
  const openEdit = (c) => {
    setForm({
      dealership_id: c.dealership_id || '', labor_code: c.labor_code || '',
      description: c.description || '', labor_price: c.labor_price ?? '',
      payout_price: c.payout_price ?? '', condition: c.condition || 'all',
      auto_detect_type: c.auto_detect_type || '', taxable: !!c.taxable,
    });
    setIsClone(false);
    setEditing(c);
  };
  // Clone = copy an existing line into the Add form (new code suffix avoids a
  // same-dealer collision). Kim tweaks the dealer/condition/price, then Save = POST.
  const openClone = (c) => {
    setForm({
      dealership_id: c.dealership_id || '', labor_code: `${c.labor_code || ''}_copy`,
      description: c.description || '', labor_price: c.labor_price ?? '',
      payout_price: c.payout_price ?? '', condition: c.condition || 'all',
      auto_detect_type: c.auto_detect_type || '', taxable: !!c.taxable,
    });
    setIsClone(true);
    setEditing('new');
  };

  const save = async () => {
    const h = authHeader();
    if (!h) return;
    const payload = {
      labor_code: form.labor_code.trim(),
      description: form.description.trim(),
      labor_price: parseFloat(form.labor_price) || 0,
      payout_price: form.payout_price === '' ? null : (parseFloat(form.payout_price) || 0),
      condition: form.condition,
      taxable: !!form.taxable,
      auto_detect_type: form.auto_detect_type || null,
      dealership_id: form.dealership_id === '' ? null : Number(form.dealership_id),
    };
    if (!payload.labor_code) { setError('Labor code is required'); return; }
    try {
      const isNew = editing === 'new';
      const url = isNew ? `${API_BASE}/photographer/labor-codes`
                        : `${API_BASE}/photographer/labor-codes/${editing.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        const fe = e?.error?.details?.errors?.[0];
        setError(fe ? `${fe.field}: ${fe.message}` : (e?.error?.message || e?.detail || 'Save failed'));
        return;
      }
      setEditing(null);
      setMessage(isNew ? 'Rate added' : 'Rate updated');
      setTimeout(() => setMessage(''), 2500);
      loadCodes();
    } catch (e) { setError('Save failed'); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Deactivate "${c.labor_code}" — ${c.description}?`)) return;
    const h = authHeader();
    if (!h) return;
    const res = await fetch(`${API_BASE}/photographer/labor-codes/${c.id}`, { method: 'DELETE', headers: h });
    if (res.ok) { setMessage('Rate deactivated'); setTimeout(() => setMessage(''), 2500); loadCodes(); }
    else setError('Delete failed');
  };

  const loadSheet = (id) => {
    const h = authHeader();
    if (!h || !id) { setSheet([]); return; }
    setSheetBusy(true);
    fetch(`${API_BASE}/admin/billing/rate-sheet?dealer_id=${id}`, { headers: h })
      .then(json)
      // `applied` (the checkbox) starts from `assigned` — whether this dealer already
      // has a configured row for the code. Toggling it drives upsert vs deactivate on save.
      .then(d => setSheet((d.rows || []).map(r => ({ ...r, applied: !!r.assigned, bill: fmt2(r.bill), payout: fmt2(r.payout) }))))
      .catch(() => setError('Failed to load rate sheet'))
      .finally(() => setSheetBusy(false));
  };

  // Copy another dealer's configured rates into the current grid (in memory — not saved
  // until Save All). Onboarding accelerator: copy a similar dealer, tweak, save.
  const copyRatesFrom = (fromId) => {
    const h = authHeader();
    if (!h || !fromId) return;
    setSheetBusy(true);
    fetch(`${API_BASE}/admin/billing/rate-sheet?dealer_id=${fromId}`, { headers: h })
      .then(json)
      .then(d => {
        const src = {};
        (d.rows || []).forEach(r => { src[r.labor_code] = r; });
        setSheet(s => s.map(r => {
          const o = src[r.labor_code];
          return o ? { ...r, applied: !!o.assigned, bill: fmt2(o.bill), payout: fmt2(o.payout) } : r;
        }));
        setMessage(`Copied rates from ${dealerName(fromId)} — review and Save All to apply`);
        setTimeout(() => setMessage(''), 5000);
      })
      .catch(() => setError('Failed to copy rates'))
      .finally(() => setSheetBusy(false));
  };
  const setCell = (i, key, val) => setSheet(s => s.map((r, j) => j === i ? { ...r, [key]: val } : r));

  // Setting a Service (auto_detect_type) — mirror the backend's "one default per
  // (Service × condition)" rule LIVE: if another same-condition code already holds this
  // Service, clear it there on-screen (with a notice) so the hand-off isn't a surprise
  // on save. (Matches save_rate_sheet's uniqueness clear.)
  const setService = (i, val) => {
    const newVal = val || null;
    const cur = sheet;
    const cond = cur[i] && cur[i].condition;
    const movedFrom = newVal
      ? cur.find((r, j) => j !== i && r.condition === cond && r.auto_detect_type === newVal)
      : null;
    setSheet(s => s.map((r, j) => {
      if (j === i) return { ...r, auto_detect_type: newVal };
      if (movedFrom && r.labor_code === movedFrom.labor_code) return { ...r, auto_detect_type: null };
      return r;
    }));
    if (movedFrom) {
      const label = (SERVICES.find(x => x.v === newVal) || {}).label || newVal;
      setMessage(`Moved "${label}" (${cond}) from ${movedFrom.labor_code} to ${cur[i].labor_code}`);
      setTimeout(() => setMessage(''), 6000);
    }
  };

  // Per-dealer SLA override (days to first photo). Blank = use the business default.
  const loadSla = (id) => {
    const h = authHeader();
    if (!h || !id) { setSla(''); return; }
    fetch(`${API_BASE}/admin/dealers/${id}`, { headers: h })
      .then(json)
      .then(d => { setSla(d?.photo_sla_days ?? ''); setStickers(!!d?.provides_stickers); })
      .catch(() => { setSla(''); setStickers(false); });
  };

  // Per-dealer "uses window stickers" flag (Buyers Guide rides with the sticker).
  // Saves immediately on toggle.
  const saveStickers = async (next) => {
    const h = authHeader();
    if (!h || !sheetDealer) return;
    setStickers(next);            // optimistic
    setStickersBusy(true);
    try {
      await fetch(`${API_BASE}/admin/dealers/${sheetDealer}/provides-stickers`, {
        method: 'PUT', headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify({ provides_stickers: next }),
      }).then(json);
      setMessage(`Stickers ${next ? 'on' : 'off'} for ${dealerName(sheetDealer)}`);
    } catch (e) {
      setStickers(!next);         // revert on failure
      setError('Failed to save stickers setting');
    } finally {
      setStickersBusy(false);
    }
  };
  const saveSla = async () => {
    const h = authHeader();
    if (!h || !sheetDealer) return;
    setSlaBusy(true);
    try {
      await fetch(`${API_BASE}/admin/dealers/${sheetDealer}/sla`, {
        method: 'PUT', headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify({ photo_sla_days: sla === '' ? null : Number(sla) }),
      }).then(json);
      setMessage(`SLA ${sla === '' ? 'cleared (uses business default)' : sla + ' days'} for ${dealerName(sheetDealer)}`);
    } catch (e) {
      setError('Failed to save SLA');
    } finally {
      setSlaBusy(false);
    }
  };

  // Reorder the product list (global default order for ALL dealers).
  const moveRow = (i, dir) => setSheet(s => {
    const j = i + dir;
    if (j < 0 || j >= s.length) return s;
    const n = [...s];
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });
  const saveOrder = async () => {
    const h = authHeader();
    if (!h) return;
    setSheetBusy(true);
    try {
      const res = await fetch(`${API_BASE}/admin/billing/catalog-order`, {
        method: 'POST', headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify(sheet.map(r => r.labor_code)),
      });
      if (!res.ok) { setError('Save order failed'); return; }
      await res.json();
      setMessage('Saved product order — now the default for all dealers');
      setTimeout(() => setMessage(''), 3500);
      setReorder(false);
    } catch (e) { setError('Save order failed'); }
    finally { setSheetBusy(false); }
  };

  const saveSheet = async () => {
    const h = authHeader();
    if (!h || !sheetDealer) return;
    setSheetBusy(true);
    const items = sheet.map(r => ({
      labor_code: r.labor_code,
      assigned: !!r.applied,
      bill: r.bill === '' || r.bill == null ? 0 : Number(r.bill),
      payout: r.payout === '' || r.payout == null ? null : Number(r.payout),
      auto_detect_type: r.auto_detect_type || null,
    }));
    try {
      const res = await fetch(`${API_BASE}/admin/billing/rate-sheet?dealer_id=${sheetDealer}`, {
        method: 'POST', headers: { ...h, 'Content-Type': 'application/json' }, body: JSON.stringify(items),
      });
      if (!res.ok) { setError('Save failed'); return; }
      const d = await res.json();
      const extra = d.deactivated ? `, ${d.deactivated} removed` : '';
      setMessage(`Saved ${d.saved} rate(s)${extra} for ${dealerName(sheetDealer)}`);
      setTimeout(() => setMessage(''), 3000);
      loadSheet(sheetDealer);
    } catch (e) { setError('Save failed'); }
    finally { setSheetBusy(false); }
  };

  const importCsv = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const h = authHeader();
    if (!h) { if (importRef.current) importRef.current.value = ''; return; }
    if (omitMode === 'deactivate' && !window.confirm(
      'Deactivate mode: for every dealer that appears in this CSV, any of their rates NOT in the file ' +
      'will be DEACTIVATED (reversible; other dealers are untouched). Continue?')) {
      if (importRef.current) importRef.current.value = '';
      return;
    }
    setLoading(true);
    const fd = new FormData();
    fd.append('file', f);
    fetch(`${API_BASE}/admin/billing/rate-import?omitted=${omitMode}`, { method: 'POST', headers: h, body: fd })
      .then(json)
      .then(d => {
        const parts = [`${d.updated} updated`, `${d.blank_kept} blank kept`];
        if (d.not_found) parts.push(`${d.not_found} not matched`);
        if (d.errors) parts.push(`${d.errors} errors`);
        if (d.bad_rows) parts.push(`${d.bad_rows} unrecognized`);
        if (d.deactivated) parts.push(`${d.deactivated} deactivated`);
        const summary = `Import: ${parts.join(', ')}.`;
        if (d.updated === 0) {
          setError(`${summary} Nothing saved — check that the Bill column has numbers and you imported a fresh export (with the ID column).`);
        } else {
          setError('');
          setMessage(summary);
          setTimeout(() => setMessage(''), 9000);
        }
        loadCodes();
      })
      .catch(() => setError('Import failed'))
      .finally(() => { setLoading(false); if (importRef.current) importRef.current.value = ''; });
  };

  const exportCsv = async () => {
    const h = authHeader();
    if (!h) return;
    const res = await fetch(`${API_BASE}/photographer/labor-codes`, { headers: h }); // all rates
    const all = await res.json();
    const rows = (Array.isArray(all) ? all : []).sort((a, b) =>
      (a.dealership_name || 'ZZZ').localeCompare(b.dealership_name || 'ZZZ') || a.labor_code.localeCompare(b.labor_code));
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['ID', 'Dealer', 'Labor Code', 'Description', 'Condition', 'Service', 'Bill Rate', 'Payout', 'Taxable'];
    const lines = [header.join(',')].concat(rows.map(r => [
      r.id, r.dealership_name || 'Default (all dealers)', r.labor_code, r.description, r.condition,
      r.auto_detect_type || '', r.labor_price, (r.payout_price ?? ''), r.taxable ? 'Y' : 'N',
    ].map(esc).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `rate-cards-by-dealer-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ padding: 20, maxWidth: 1150, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Rate Cards (Labor Codes)</h2>
      </div>
      <p style={{ color: '#6b7280', marginTop: 0 }}>
        Per-dealer billing rate (what the dealer pays) and photographer payout (what the shooter earns).
        Recovered from the competitor feeds; fill any blank payouts here.
      </p>
      {error && <div style={{ color: '#dc2626', marginBottom: 8 }}>{error} <button onClick={() => setError('')} style={linkBtn}>dismiss</button></div>}
      {message && <div style={{ color: '#059669', marginBottom: 8 }}>{message}</div>}

      <div style={{ display: 'flex', gap: 6, margin: '4px 0 12px' }}>
        <button onClick={() => setView('sheet')} style={view === 'sheet' ? tabActive : tab}>📋 Rate Sheet (by dealer)</button>
        <button onClick={() => setView('list')} style={view === 'list' ? tabActive : tab}>☰ All Rates</button>
      </div>

      {view === 'list' && (<>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', margin: '12px 0' }}>
        <select value={dealerId} onChange={e => setDealerId(e.target.value)} style={ctrl}>
          <option value="">All rate cards</option>
          {dealers.map(d => <option key={d.id} value={d.id}>{d.dealership_name || d.name}</option>)}
        </select>
        <button onClick={openAdd} style={primaryBtn}>+ Add Rate</button>
        <button onClick={exportCsv} style={btn}>⬇ Export CSV (by dealer)</button>
        <select value={omitMode} onChange={e => setOmitMode(e.target.value)} style={ctrl}
          title="What to do with rows you delete from the CSV before importing">
          <option value="skip">Omitted rows: keep</option>
          <option value="deactivate">Omitted rows: deactivate</option>
        </select>
        <label style={{ ...btn, cursor: 'pointer' }}>⬆ Import filled CSV
          <input ref={importRef} type="file" accept=".csv" onChange={importCsv} style={{ display: 'none' }} />
        </label>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{codes.length} rate(s)</span>
      </div>
      <div style={{ fontSize: 12.5, color: '#475569', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', margin: '0 0 14px', maxWidth: 820, lineHeight: 1.5 }}>
        <b>CSV round-trip:</b> Export → fill the <b>Bill</b> column in Excel → Import. Rows match on the
        <b> ID</b> column (don't edit it). A blank Bill leaves that rate unchanged.<br />
        <b>Rows you delete from the CSV</b> are controlled by the dropdown:&nbsp;
        <b>Keep</b> = omitted rows are left untouched (safe default).&nbsp;
        <b>Deactivate</b> = for each dealer that appears in the file, any of their rates you removed get
        deactivated (reversible; other dealers and the global catalog are never touched).
      </div>

      {loading ? <div>Loading…</div> : (
        <table style={table}>
          <thead>
            <tr>{['Dealer', 'Code', 'Description', 'Condition', 'Service', 'Bill', 'Payout', ''].map(h =>
              <th key={h} style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {codes.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid #eef2f7' }}>
                <td style={td}>{c.dealership_name || <em style={{ color: '#94a3b8' }}>Default</em>}</td>
                <td style={td}>{c.labor_code}</td>
                <td style={td}>{c.description}</td>
                <td style={td}>{c.condition}</td>
                <td style={td}>{(SERVICES.find(s => s.v === (c.auto_detect_type || '')) || {}).label || c.auto_detect_type || ''}</td>
                <td style={td}>{c.labor_price != null ? `$${Number(c.labor_price).toFixed(2)}` : '—'}</td>
                <td style={{ ...td, color: c.payout_price == null ? '#dc2626' : '#111827' }}>
                  {c.payout_price != null ? `$${Number(c.payout_price).toFixed(2)}` : '⚠ set'}</td>
                <td style={td}>
                  <button onClick={() => openEdit(c)} style={linkBtn}>Edit</button>{' '}
                  <button onClick={() => openClone(c)} style={linkBtn}>Clone</button>{' '}
                  <button onClick={() => remove(c)} style={{ ...linkBtn, color: '#dc2626' }}>Delete</button>
                </td>
              </tr>
            ))}
            {!codes.length && <tr><td colSpan={8} style={{ ...td, color: '#6b7280' }}>No rates.</td></tr>}
          </tbody>
        </table>
      )}
      </>)}

      {view === 'sheet' && (
        <div>
          <p style={{ color: '#6b7280', marginTop: 0, fontSize: 13 }}>
            Pick a dealer to see <b>every approved product</b>. <b>Check</b> the ones that apply to
            this dealer, set each <b>Bill</b> (what the dealer pays) and <b>Payout</b> (what the
            shooter earns), then <b>Save All</b>. Unchecked = not billed for this dealer.
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '12px 0', flexWrap: 'wrap' }}>
            <span style={{ color: '#6b7280', fontSize: 13 }}>Dealer:</span>
            <select value={sheetDealer} onChange={e => { setSheetDealer(e.target.value); setCopyFrom(''); setShowInactive(false); loadSheet(e.target.value); loadSla(e.target.value); }} style={ctrl}>
              <option value="">— select dealer —</option>
              {dealers.map(d => <option key={d.id} value={d.id}>{d.dealership_name || d.name}</option>)}
            </select>
            {sheetDealer && (
              <>
                <span style={{ color: '#6b7280', fontSize: 13, marginLeft: 8 }}>Copy rates from:</span>
                <select value={copyFrom} onChange={e => { setCopyFrom(e.target.value); copyRatesFrom(e.target.value); }} style={ctrl}>
                  <option value="">— dealer —</option>
                  {dealers.filter(d => String(d.id) !== String(sheetDealer)).map(d =>
                    <option key={d.id} value={d.id}>{d.dealership_name || d.name}</option>)}
                </select>
                <button onClick={() => setReorder(r => !r)} style={reorder ? primaryBtn : btn}>
                  {reorder ? '✓ Done reordering' : '⇅ Reorder'}
                </button>
                {reorder && (
                  <button onClick={saveOrder} disabled={sheetBusy} style={primaryBtn}>💾 Save order as default</button>
                )}
              </>
            )}
          </div>
          {sheetDealer && reorder && (
            <div style={{ fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a',
                          borderRadius: 6, padding: '8px 12px', margin: '0 0 10px' }}>
              Use ▲▼ to reorder, then <b>Save order as default</b>. This sets the product order for <b>every dealer</b> (it's the global catalog order, not per-dealer).
            </div>
          )}
          {sheetDealer && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '0 0 12px',
                          padding: '10px 12px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 6 }}>
              <span style={{ fontWeight: 600 }}>Photo SLA override:</span>
              <input type="number" min="1" max="365" value={sla} placeholder="default"
                onChange={e => setSla(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ ...ctrl, width: 90 }} />
              <span style={{ color: '#6b7280', fontSize: 13 }}>days to first photo (blank = business default)</span>
              <button onClick={saveSla} disabled={slaBusy} style={primaryBtn}>
                {slaBusy ? 'Saving…' : 'Save SLA'}
              </button>
            </div>
          )}
          {sheetDealer && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '0 0 12px',
                          padding: '10px 12px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 6 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontWeight: 600 }}>
                <input type="checkbox" checked={stickers} disabled={stickersBusy}
                  onChange={e => saveStickers(e.target.checked)} />
                Uses window stickers
              </label>
              <span style={{ color: '#6b7280', fontSize: 13 }}>
                Buyers Guide rides with the sticker. Seeded from sticker rate codes; toggle as needed.
              </span>
            </div>
          )}
          {sheetDealer && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '6px 0 10px', flexWrap: 'wrap', gap: 10 }}>
              <h3 style={{ margin: 0 }}>Current Dealership: {dealerName(sheetDealer)}</h3>
              <span style={{ display: 'flex', gap: 12, alignItems: 'center', color: '#6b7280', fontSize: 13 }}>
                {/* Both directions name their count, so the view describes itself rather
                    than leaving the reader to wonder whether a short table is the whole
                    catalogue. */}
                {!reorder && (showInactive
                  ? (sheet.some(r => !r.applied) && (
                      <button onClick={() => setShowInactive(false)} style={linkBtn}>
                        Hide the {sheet.filter(r => !r.applied).length} not applied
                      </button>
                    ))
                  : (sheet.some(r => !r.applied) && (
                      <button onClick={() => setShowInactive(true)} style={addCodesBtn}>
                        + Show all {sheet.length} products ({sheet.filter(r => !r.applied).length} not applied)
                      </button>
                    ))
                )}
                <span>{sheet.filter(r => r.applied).length} of {sheet.length} applied</span>
                {!reorder && (
                  <button onClick={saveSheet} disabled={sheetBusy} style={saveBarBtn}>
                    {sheetBusy ? 'Saving…' : '💾 Save All'}
                  </button>
                )}
              </span>
            </div>
          )}

          {!sheetDealer ? <div style={{ color: '#6b7280' }}>Select a dealer to price their rate sheet.</div>
            : sheetBusy && !sheet.length ? <div>Loading…</div> : (
            <table style={table}>
              <thead><tr>{[...(reorder ? ['Move'] : []), 'Apply', 'Code', 'Product', 'Cond', 'Service', 'Dealer Bill ($)', 'Photographer Payout ($)'].map(h =>
                <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {sheet.map((r, i) => {
                  const on = !!r.applied;
                  // Hide codes not applied to this dealer when "Show inactive" is off
                  // (always show all while reordering — the order is the global catalog).
                  if (!showInactive && !reorder && !on) return null;
                  const dim = on ? '#111827' : '#9ca3af';
                  const inpStyle = { ...cellInp, background: on ? '#fff' : '#f1f5f9', color: dim };
                  return (
                  <tr key={r.labor_code} style={{ borderBottom: '1px solid #eef2f7', background: on ? '#fff' : '#fafbfc' }}>
                    {reorder && (
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <button onClick={() => moveRow(i, -1)} disabled={i === 0} style={moveBtn} title="Move up">▲</button>
                        <button onClick={() => moveRow(i, 1)} disabled={i === sheet.length - 1} style={moveBtn} title="Move down">▼</button>
                      </td>
                    )}
                    <td style={{ ...td, textAlign: 'center' }}>
                      <input type="checkbox" checked={on} onChange={e => setCell(i, 'applied', e.target.checked)}
                        style={{ width: 17, height: 17, cursor: 'pointer' }} />
                    </td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12, color: dim }}>{r.labor_code}</td>
                    <td style={{ ...td, color: dim }}>{r.description}</td>
                    <td style={{ ...td, color: dim }}>{r.condition}</td>
                    <td style={td}>
                      <select value={r.auto_detect_type || ''} disabled={!on}
                        onChange={e => setService(i, e.target.value)}
                        style={{ ...cellInp, fontSize: 12, background: on ? '#fff' : '#f1f5f9', color: on ? '#111827' : '#9ca3af' }}
                        title="Designate this as the dealer's default code for photos / video / photos+video (drives the app's auto-bill)">
                        {SERVICES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
                      </select>
                    </td>
                    <td style={td}>
                      <span style={moneyWrap}>
                        <span style={{ ...moneyDollar, color: on ? '#6b7280' : '#cbd5e1' }}>$</span>
                        <input type="text" inputMode="decimal" value={r.bill ?? ''} placeholder="0.00" disabled={!on}
                          onChange={e => setCell(i, 'bill', e.target.value)}
                          onBlur={e => setCell(i, 'bill', fmt2(e.target.value))}
                          style={{ ...inpStyle, ...moneyInp, width: 90 }} />
                      </span>
                    </td>
                    <td style={td}>
                      <span style={moneyWrap}>
                        <span style={{ ...moneyDollar, color: on ? '#6b7280' : '#cbd5e1' }}>$</span>
                        <input type="text" inputMode="decimal" value={r.payout ?? ''} placeholder="0.00" disabled={!on}
                          onChange={e => setCell(i, 'payout', e.target.value)}
                          onBlur={e => setCell(i, 'payout', fmt2(e.target.value))}
                          style={{ ...inpStyle, ...moneyInp, width: 80 }} />
                      </span>
                    </td>
                  </tr>
                  );
                })}
                {/* Only reachable now if someone has deliberately collapsed the view on a
                    dealer with nothing applied — the default shows every product, so an
                    unconfigured dealer is a full list of empty boxes rather than a blank
                    table with no way forward. */}
                {!showInactive && !reorder && !sheet.some(r => r.applied) && (
                  <tr><td colSpan={7} style={{ ...td, color: '#6b7280' }}>
                    No codes applied to this dealer yet — tap “Show all products” above to
                    tick the ones they buy.
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
          {sheetDealer && !reorder && (
            <div style={saveBar}>
              <span style={{ color: '#374151', fontSize: 13 }}>
                {sheet.filter(r => r.applied).length} of {sheet.length} codes applied
              </span>
              <button onClick={saveSheet} disabled={sheetBusy} style={saveBarBtn}>
                {sheetBusy ? 'Saving…' : '💾 Save All Rate Changes'}
              </button>
            </div>
          )}
        </div>
      )}

      {editing && (
        <div style={overlay} onClick={() => setEditing(null)}>
          <div style={modal} onClick={e => e.stopPropagation()}>
            <h3>{editing === 'new' ? (isClone ? 'Clone Rate' : 'Add Rate') : 'Edit Rate'}</h3>
            <label style={lbl}>Dealer
              <select value={form.dealership_id} onChange={e => setForm({ ...form, dealership_id: e.target.value })} style={inp}>
                <option value="">Default (all dealers)</option>
                {dealers.map(d => <option key={d.id} value={d.id}>{d.dealership_name || d.name}</option>)}
              </select>
            </label>
            <label style={lbl}>Labor code
              <input value={form.labor_code} onChange={e => setForm({ ...form, labor_code: e.target.value })} style={inp} placeholder="e.g. DLR-00062_photo_video_new" />
            </label>
            <label style={lbl}>Description
              <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={inp} placeholder="Photo & Video (New)" />
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <label style={{ ...lbl, flex: 1 }}>Bill rate ($)
                <input type="number" step="0.01" value={form.labor_price} onChange={e => setForm({ ...form, labor_price: e.target.value })} style={inp} />
              </label>
              <label style={{ ...lbl, flex: 1 }}>Payout ($)
                <input type="number" step="0.01" value={form.payout_price} onChange={e => setForm({ ...form, payout_price: e.target.value })} style={inp} placeholder="(blank = unset)" />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <label style={{ ...lbl, flex: 1 }}>Condition
                <select value={form.condition} onChange={e => setForm({ ...form, condition: e.target.value })} style={inp}>
                  {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label style={{ ...lbl, flex: 1 }}>Service (auto-detect)
                <input list="service-opts" value={form.auto_detect_type}
                  onChange={e => setForm({ ...form, auto_detect_type: e.target.value })}
                  style={inp} placeholder="type or pick — e.g. photos_and_video" />
                <datalist id="service-opts">
                  {SERVICES.filter(s => s.v).map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
                </datalist>
              </label>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '8px 0', fontSize: 13, color: '#6b7280' }}>
              <input type="checkbox" checked={form.taxable} onChange={e => setForm({ ...form, taxable: e.target.checked })} /> Taxable
            </label>
            {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{error} <button onClick={() => setError('')} style={linkBtn}>dismiss</button></div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button onClick={save} style={primaryBtn}>Save</button>
              <button onClick={() => setEditing(null)} style={btn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ctrl = { padding: 8, border: '1px solid #d1d5db', borderRadius: 6 };
const tab = { padding: '7px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 14 };
const tabActive = { ...tab, background: '#0078D4', color: '#fff', border: 'none', fontWeight: 600 };
const cellInp = { padding: '5px 7px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 13 };
const moveBtn = { padding: '2px 7px', margin: '0 2px', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 12 };
const moneyWrap = { position: 'relative', display: 'inline-flex', alignItems: 'center' };
const moneyDollar = { position: 'absolute', left: 7, fontSize: 13, pointerEvents: 'none' };
const moneyInp = { paddingLeft: 16, textAlign: 'right' };
const btn = { padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' };
const primaryBtn = { ...btn, background: '#0078D4', color: '#fff', border: 'none' };
const linkBtn = { background: 'none', border: 'none', color: '#0078D4', cursor: 'pointer', padding: 0 };
const addCodesBtn = { background: '#0078D4', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const saveBar = { position: 'sticky', bottom: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '12px 14px', marginTop: 10, background: '#fff', borderTop: '2px solid #16a34a', boxShadow: '0 -2px 8px rgba(0,0,0,0.08)' };
const saveBarBtn = { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 15, fontWeight: 700, cursor: 'pointer' };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 14 };
const th = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: '8px 10px', verticalAlign: 'top' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 };
const modal = { background: '#fff', borderRadius: 10, padding: 22, width: 440, maxWidth: '92vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' };
const lbl = { display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 8 };
const inp = { width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box', marginTop: 2 };
